import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { GitBranch, Upload } from "lucide-react";

import AuthPage from "../../components/auth/AuthPage";
import SubmitButton from "../../components/auth/SubmitButton";
import LoginSucessModal from "../../components/LoginSucessModal";
import LogoUploadField from "../../components/LogoUploadField";
import PageAvatar from "../../components/PageAvatar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { API } from "../../constant/api";
import { options } from "../../constant";
import sessionAxios from "../../utils/sessionAxios";
import { useUser } from "../../context/user-context";
import { useBusinessImageUpload } from "../../hooks/use-business-image-upload";
import {
  BusinessListItem,
  createBusinessBlockedMessage,
  useBusinesses,
} from "../../hooks/use-businesses";
import UploadDocuments from "../settings/UploadDocuments";
import {
  branchProfileSchema,
  businessProfileSchema,
} from "../../utils/authSchemas";
import { cn } from "../../utils/cn";
import { errorMessage } from "../../utils/errorMessage";

const BUSINESS_TYPES = [
  {
    value: "STARTER",
    title: "Starter Business",
    description:
      "Yet to be registered officially. We will need to verify this business.",
  },
  {
    value: "REGISTERED",
    title: "Registered Business",
    description:
      "Registered officially with documentation, licenses and approval to operate legally.",
  },
  {
    value: "BRANCH",
    title: "Branch",
    description:
      "Create a branch of an existing business. Inherits verification and details from the parent.",
    icon: GitBranch,
  },
];

const SIZES = ["1-10", "11-50", "51-100", "101 +"];

type BusinessProfileValues = {
  name: string;
  industry: string;
  email: string;
  address: string;
  size: string;
  country: string;
  description: string;
};

type BranchProfileValues = {
  name: string;
  address: string;
  size: string;
  description: string;
  managerEmail: string;
};

const BusinessProfile = () => {
  const {
    setUploadModal,
    uploadModal,
    setBusinessVerified,
    identityUrl,
    addressUrl,
    cacUrl,
    successModal,
    setSuccessModal,
    bnNumber,
  } = useUser();
  const {
    businesses,
    hasBusiness,
    canCreateMore,
    blockedReason,
    selectBusiness,
    isLoading: loadingBusinesses,
  } = useBusinesses();
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [businessType, setBusinessType] = useState("STARTER");
  const [parentBusinessId, setParentBusinessId] = useState("");
  const logo = useBusinessImageUpload({ documentType: "LOGO" });

  const isBranch = businessType === "BRANCH";
  const isRegistered = businessType === "REGISTERED";

  const parentCandidates = useMemo(
    () =>
      (businesses ?? []).filter(
        (b: BusinessListItem) =>
          !b.parentBusinessId &&
          ["APPROVED", "VERIFIED"].includes(
            String(b.status ?? "").toUpperCase()
          )
      ),
    [businesses]
  );

  const selectedParent = useMemo(
    () => parentCandidates.find((b) => b._id === parentBusinessId),
    [parentCandidates, parentBusinessId]
  );

  const form = useForm<BusinessProfileValues>({
    resolver: yupResolver(businessProfileSchema) as any,
    defaultValues: {
      name: "",
      industry: "",
      email: "",
      address: "",
      size: "",
      country: "",
      description: "",
    },
    mode: "onTouched",
  });

  const branchForm = useForm<BranchProfileValues>({
    resolver: yupResolver(branchProfileSchema) as any,
    defaultValues: {
      name: "",
      address: "",
      size: "",
      description: "",
      managerEmail: "",
    },
    mode: "onTouched",
  });

  useEffect(() => {
    if (!isBranch || !selectedParent) return;
    branchForm.setValue("size", selectedParent.details?.size ?? "");
    branchForm.setValue(
      "description",
      selectedParent.details?.description ?? ""
    );
  }, [isBranch, selectedParent, branchForm]);

  const isAdditionalBusiness = hasBusiness;
  const creationBlocked = !loadingBusinesses && hasBusiness && !canCreateMore;

  const onSubmitBranch = (values: BranchProfileValues) => {
    if (loading) return;
    if (!parentBusinessId) {
      toast.error("Select the parent business for this branch");
      return;
    }
    setLoading(true);

    const payload: Record<string, any> = {
      name: values.name,
      details: {
        address: values.address,
        ...(values.description && { description: values.description }),
        ...(values.size && { size: values.size }),
      },
    };
    if (values.managerEmail) {
      payload.managerEmail = values.managerEmail;
    }

    sessionAxios
      .post(API.createBranch(parentBusinessId), payload)
      .then((r: any) => {
        setLoading(false);
        const createdId = r?.data?.data?.business?._id as string | undefined;
        if (createdId) {
          selectBusiness(createdId);
        }
        queryClient.invalidateQueries({ queryKey: ["get-businesses"] });
        toast.success("Branch created successfully");
        navigate("/dashboard");
      })
      .catch((e: any) => {
        setLoading(false);
        toast.error(
          errorMessage(
            e?.response?.data?.message || "An error occurred. Please try again"
          )
        );
      });
  };

  const onSubmit = (values: BusinessProfileValues) => {
    if (loading) return;
    if (creationBlocked) {
      toast.error(createBusinessBlockedMessage(blockedReason));
      return;
    }
    if (logo.isUploading) {
      toast.error("Hold on — your logo is still uploading");
      return;
    }
    if (!logo.objectURL) {
      toast.error("Upload your business logo to continue");
      return;
    }
    setLoading(true);

    const payload = {
      name: values.name,
      email: values.email,
      logo: logo.objectURL,
      type: businessType,
      details: {
        industry: values.industry,
        address: values.address,
        size: values.size,
        country: values.country,
        description: values.description,
      },
      ...(isRegistered && {
        bnNumber,
        documents: {
          identity: identityUrl,
          proofOfAddress: addressUrl,
          cac: cacUrl,
        },
      }),
    };

    sessionAxios
      .post(API.createBusiness, payload)
      .then((r: any) => {
        setLoading(false);
        const createdId = r?.data?.data?.business?._id as string | undefined;
        if (createdId) {
          selectBusiness(createdId);
        }
        queryClient.invalidateQueries({ queryKey: ["get-businesses"] });
        toast.success("Business successfully created");
        setBusinessVerified(true);
        setSuccessModal(false);
        navigate("/dashboard");
      })
      .catch((e: any) => {
        setLoading(false);
        toast.error(
          errorMessage(
            e?.response?.data?.message || "An error occurred. Please try again"
          )
        );
      });
  };

  const pageTitle = isBranch
    ? "Create a branch"
    : isAdditionalBusiness
      ? "Add a business"
      : "Business profile";

  const pageDescription = isBranch
    ? "Open a new branch under one of your existing businesses."
    : isAdditionalBusiness
      ? "Create another business under this account. You can switch between them anytime."
      : "Set up your business. Make sure all details are correct.";

  return (
    <AuthPage
      width="wide"
      title={pageTitle}
      description={
        <span className="flex flex-col justify-between gap-2 lgss:flex-row lgss:items-center">
          <span>{pageDescription}</span>
          <span className="text-[12px]">
            {isAdditionalBusiness || isBranch ? "Back to " : "Will do this later, take me to "}
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="font-semibold text-primary underline"
            >
              Dashboard
            </button>
          </span>
        </span>
      }
    >
      {creationBlocked && !isBranch && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-medium text-amber-900">
          {createBusinessBlockedMessage(blockedReason)}
        </div>
      )}

      {/* ── Business type selector ── */}
      <fieldset className="mb-5">
        <legend className="sr-only">Business type</legend>
        <div className="grid grid-cols-1 gap-4 lgss:grid-cols-3">
          {BUSINESS_TYPES.map((type) => {
            const selected = businessType === type.value;
            if (type.value === "BRANCH" && !hasBusiness) return null;
            return (
              <label
                key={type.value}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors",
                  selected
                    ? "border-primary bg-accent"
                    : "border-input bg-muted/50 hover:bg-muted"
                )}
              >
                <input
                  type="radio"
                  name="businessType"
                  value={type.value}
                  checked={selected}
                  onChange={() => setBusinessType(type.value)}
                  className="mt-1 size-4 accent-primary"
                />
                <span>
                  <span className="flex items-center gap-1.5 text-[14px] font-bold text-foreground">
                    {"icon" in type && type.icon && (
                      <type.icon className="size-4 text-primary" />
                    )}
                    {type.title}
                  </span>
                  <span className="mt-1 block text-[13px] font-medium text-muted-foreground">
                    {type.description}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* ── Branch creation form ── */}
      {isBranch ? (
        <Form {...branchForm}>
          <form
            onSubmit={branchForm.handleSubmit(onSubmitBranch)}
            className="space-y-5"
          >
            {/* Parent business selector */}
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                Parent business
              </label>
              {parentCandidates.length === 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-medium text-amber-900">
                  You don't have any verified businesses yet. Create and verify
                  a business first, then you can add branches to it.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 mds:grid-cols-2">
                  {parentCandidates.map((biz) => {
                    const selected = parentBusinessId === biz._id;
                    return (
                      <button
                        key={biz._id}
                        type="button"
                        onClick={() => setParentBusinessId(biz._id)}
                        className={cn(
                          "flex items-center gap-3 rounded-xl border p-3 text-left transition-colors",
                          selected
                            ? "border-primary bg-accent ring-1 ring-primary/20"
                            : "border-input bg-muted/50 hover:bg-muted"
                        )}
                      >
                        <PageAvatar
                          name={biz.name}
                          image={biz.logo}
                          className="size-9"
                          textClassName="text-[12px]"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14px] font-semibold text-foreground">
                            {biz.name || "Untitled"}
                          </span>
                          <span className="block truncate text-[12px] font-medium text-muted-foreground">
                            {biz.type} · {biz.details?.address}
                          </span>
                        </span>
                        {selected && (
                          <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
                            Selected
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {parentBusinessId && (
              <>
                <FormField
                  control={branchForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Branch name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Acme Lekki"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={branchForm.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Branch address</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Full address of this branch"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-5 lgss:grid-cols-2">
                  <FormField
                    control={branchForm.control}
                    name="size"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Size{" "}
                          <span className="text-muted-foreground">
                            (inherited from parent)
                          </span>
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="h-[50px] rounded-xl bg-muted/50">
                              <SelectValue placeholder="Select branch size" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="font-manrope">
                            {SIZES.map((size) => (
                              <SelectItem key={size} value={size}>
                                {size} employees
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={branchForm.control}
                    name="managerEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Manager email{" "}
                          <span className="text-muted-foreground">
                            (optional)
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Invite a manager for this branch"
                            {...field}
                            onChange={(e) =>
                              field.onChange(e.target.value.toLowerCase())
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={branchForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Description{" "}
                        <span className="text-muted-foreground">
                          (inherited from parent)
                        </span>
                      </FormLabel>
                      <FormControl>
                        <textarea
                          placeholder="Describe this branch, or leave as-is to use the parent's description"
                          className="flex h-[89px] w-full rounded-xl border border-input bg-muted/50 px-4 py-3 text-sm transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <SubmitButton loading={loading || loadingBusinesses}>
                  Create branch
                </SubmitButton>
              </>
            )}
          </form>
        </Form>
      ) : (
        /* ── Standard business creation form ── */
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <LogoUploadField
              name={form.watch("name")}
              imageURL={logo.displayURL}
              status={logo.status}
              error={logo.error}
              onSelect={logo.upload}
              onRetry={logo.retry}
              onTooLarge={(message) => toast.error(message)}
            />

            <div className="grid grid-cols-1 gap-5 lgss:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business name</FormLabel>
                    <FormControl>
                      <Input placeholder="Business name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business industry</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-[50px] rounded-xl bg-muted/50">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[260px] font-manrope">
                        {options.map((option: any) => (
                          <SelectItem key={option.value} value={option.label}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Official email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Official email"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.toLowerCase())
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address of business</FormLabel>
                  <FormControl>
                    <Input placeholder="Address of business" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-5 lgss:grid-cols-2">
              <FormField
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Size</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-[50px] rounded-xl bg-muted/50">
                          <SelectValue placeholder="Select business size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="font-manrope">
                        {SIZES.map((size) => (
                          <SelectItem key={size} value={size}>
                            {size} employees
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-[50px] rounded-xl bg-muted/50">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="font-manrope">
                        <SelectItem value="Nigeria">Nigeria</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-5 lgss:grid-cols-2">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business description</FormLabel>
                    <FormControl>
                      <textarea
                        placeholder="Describe your business. What is it about? How long has it been in operation..."
                        className="flex h-[89px] w-full rounded-xl border border-input bg-muted/50 px-4 py-3 text-sm transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isRegistered && (
                <div className="flex flex-col">
                  <span className="mb-2 text-sm font-medium text-foreground">
                    Upload documents
                  </span>
                  <button
                    type="button"
                    onClick={() => setUploadModal((prev: any) => !prev)}
                    className="flex h-[89px] w-full flex-col items-center justify-center rounded-xl border border-dashed border-[#9AD1F9] bg-accent text-primary transition-colors hover:bg-accent/70"
                  >
                    <Upload className="size-6" />
                    <span className="mt-1 text-[14px] font-medium">
                      Upload documents
                    </span>
                  </button>
                </div>
              )}
            </div>

            <SubmitButton
              loading={loading || loadingBusinesses || logo.isUploading}
              disabled={creationBlocked}
            >
              {isAdditionalBusiness ? "Create business" : "Set up my business"}
            </SubmitButton>
          </form>
        </Form>
      )}

      {uploadModal ? <UploadDocuments /> : null}
      {successModal ? <LoginSucessModal /> : null}
    </AuthPage>
  );
};

export default BusinessProfile;
